/**
 * SectionGuide — the persistent, always-available help box at the top of every
 * workspace section. Config-driven (``SECTION_GUIDES``) and DRY: it reuses
 * ``QUICK_PROMPTS_BY_TAB`` for the example prompts. Complements the one-time
 * storyboard tour — the tour walks you through once, this stays put.
 *
 * Audience (see ``useOnboardingMode``):
 *  - ``"demo"`` — ephemeral visitors: shows everything, including the static
 *    connected-connectors list and the demo-only notes ("invii simulati",
 *    "non utilizzabile nella demo"). Always on.
 *  - ``"user"`` — real first-run users: shows the description + example prompts
 *    (generic guidance); demo-specific copy/connectors are suppressed, and the
 *    user can turn the guidance off everywhere ("Non mostrare più").
 *
 * Collapsible per section (persisted). Lucide icons only.
 */
import { cn } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Link, useRouterState } from "@tanstack/react-router";
import { Lock, Plug, Sparkles } from "lucide-react";

import { formatActiveConnectorsLabel, useActiveConnectorsCount } from "@/features/capabilities";
import type { WorkspaceTab } from "@/features/workspace-router";
import { getWorkspaceTabDef } from "@/features/workspace/config";

import { QUICK_PROMPTS_BY_TAB } from "@/features/chat/components/ChatEmptyState";
import { useSendQuery } from "@/features/chat/hooks/useSendQuery";

import { GuideBox, focusRing } from "./GuideBox";
import { useSectionGuideStore } from "./sectionGuideStore";
import { SECTION_GUIDES } from "./sectionGuides";
import { onboardingKey, useOnboardingMode } from "./useOnboardingMode";

export interface SectionGuideProps {
  section: WorkspaceTab;
}

export function SectionGuide({ section }: SectionGuideProps): React.ReactElement | null {
  const { i18n } = useLingui();
  const { send } = useSendQuery();
  const mode = useOnboardingMode();
  // Jun 17, 2026 — real users land COLLAPSED by default so the workspace
  // doesn't open with an orange wash banner above every page. Demo mode
  // still expands by default (visitors need to see what's inside). The
  // user can re-expand at any time via the chevron; the choice persists.
  const stored = useSectionGuideStore((s) => s.collapsed[section]);
  const collapsed = typeof stored === "boolean" ? stored : mode !== "demo";
  const toggle = useSectionGuideStore((s) => s.toggle);
  const dismiss = useSectionGuideStore((s) => s.dismiss);
  const audienceKey = onboardingKey();
  const dismissed = useSectionGuideStore((s) => Boolean(s.dismissed[audienceKey]));
  // Real connected-sources count (cached manifest query) — shown to real users
  // instead of the demo's static connector list, so the guide reflects reality.
  const connectorCount = useActiveConnectorsCount();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const guide = SECTION_GUIDES[section];
  // Casa landing ("/"): the Casa opens with its own verdict header — the
  // guide is for the workspace sections, where ``section`` (the persisted
  // currentTab) matches the location. At home it's stale restore-memory.
  if (pathname === "/") return null;
  if (!mode || dismissed || !guide) return null;

  const isDemo = mode === "demo";
  const def = getWorkspaceTabDef(section);
  const label = def ? i18n._(def.labelMsg) : "";
  const prompts = (QUICK_PROMPTS_BY_TAB[section] ?? []).slice(0, 3);
  // Demo-only chrome: the static connector list + the demo-unavailable note +
  // the demo-flavoured tips. Real users get description + prompts (correct copy
  // without faked connectors).
  const showConnectors = isDemo && guide.connectors.length > 0;
  const showDemoNote = isDemo && Boolean(guide.demoNote);
  const showTip = isDemo && Boolean(guide.tip);
  const showPrompts = !showDemoNote && prompts.length > 0;

  return (
    <GuideBox
      icon={Sparkles}
      // Generic for every section — the section name is already the active tab
      // right above, so repeating it here is redundant.
      title={i18n._(msg`Suggerimenti e azioni rapide`)}
      collapsed={collapsed}
      onToggle={() => toggle(section)}
      ariaLabel={i18n._(msg`Guida alla sezione ${label}`)}
      quiet
      className="mx-4 mt-3"
    >
      <div className="flex flex-col gap-3">
        <p className="max-w-2xl text-[12.5px] text-ink-2 leading-relaxed">
          {i18n._(guide.description)}
        </p>

        {showDemoNote && guide.demoNote ? (
          <div className="flex items-start gap-2 rounded-xl border border-ando-ink/10 bg-card/60 px-3 py-2">
            <Lock size={13} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-ink-3" />
            <p className="text-[12px] text-ink-2 leading-relaxed">{i18n._(guide.demoNote)}</p>
          </div>
        ) : null}

        {showTip && guide.tip ? (
          <p className="text-[12px] text-ink-3 leading-relaxed">{i18n._(guide.tip)}</p>
        ) : null}

        {showConnectors ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <Plug size={12} strokeWidth={1.75} aria-hidden className="text-ink-3" />
            {guide.connectors.map((c) => (
              <span
                key={c}
                className="rounded-full border border-ando-ink/10 bg-card/60 px-2 py-0.5 text-[11px] text-ink-2"
              >
                {c}
              </span>
            ))}
          </div>
        ) : null}

        {showPrompts ? (
          <div className="flex flex-wrap gap-1.5">
            {prompts.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() =>
                  void send({
                    text: i18n._(p.prompt),
                    // Demo chips showcase the full Corporate Brain (RAG +
                    // gestionale); real users keep their chosen focus.
                    ...(isDemo ? { configOverride: { focus: "full" as const } } : {}),
                  })
                }
                className={cn(
                  "rounded-full border border-ando-orange/30 bg-ando-orange/[0.06] px-3 py-1 font-medium text-[12px] text-ando-orange-hover transition-colors hover:border-ando-orange hover:bg-ando-orange/[0.12]",
                  focusRing,
                )}
              >
                {i18n._(p.label)}
              </button>
            ))}
          </div>
        ) : null}

        {/* Footer (real users): connector status + opt-out, set apart from the
            content with a hairline so the body reads clean. */}
        {mode === "user" ? (
          <div className="mt-0.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 border-hairline border-t pt-3">
            <Link
              to="/integrations"
              className={cn(
                "inline-flex items-center gap-1.5 text-[12px] text-ando-orange transition-colors hover:text-ando-orange/80",
                focusRing,
              )}
            >
              <Plug size={12} strokeWidth={1.75} aria-hidden />
              <span>
                {formatActiveConnectorsLabel(i18n, connectorCount)} ·{" "}
                {i18n._(msg`Gestisci i connettori`)}
              </span>
            </Link>
            <button
              type="button"
              onClick={() => dismiss(audienceKey)}
              className={cn(
                "text-[11.5px] text-ink-3 underline-offset-2 transition-colors hover:text-ink-1 hover:underline",
                focusRing,
              )}
            >
              {i18n._(msg`Non mostrare più questi aiuti`)}
            </button>
          </div>
        ) : null}
      </div>
    </GuideBox>
  );
}

SectionGuide.displayName = "SectionGuide";
